import type { CropPreset } from '../constants/imageCropPresets';

export type CropTransform = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

export type CropResult = {
  blob: Blob;
  focal: { x: number; y: number };
};

export function clampCropTransform(
  transform: CropTransform,
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number
): CropTransform {
  const cover = initialCoverScale(imgW, imgH, frameW, frameH);
  const scale = Math.max(cover, Math.min(cover * 3, transform.scale));
  const limitX = Math.max(0, (imgW * scale - frameW) / 2);
  const limitY = Math.max(0, (imgH * scale - frameH) / 2);
  return {
    scale,
    offsetX: Math.max(-limitX, Math.min(limitX, transform.offsetX)),
    offsetY: Math.max(-limitY, Math.min(limitY, transform.offsetY)),
  };
}

/** Match the editor's pan to CSS cover + zoom + transform-origin. */
export function framingFromTransform(
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
  transform: CropTransform
): { x: number; y: number; zoom: number } {
  const bounded = clampCropTransform(transform, imgW, imgH, frameW, frameH);
  const cover = initialCoverScale(imgW, imgH, frameW, frameH);
  const zoom = bounded.scale / cover;
  const axis = (offset: number, base: number, frame: number) =>
    zoom * base <= frame + 0.001
      ? 0.5
      : Math.max(0, Math.min(1, 0.5 - offset / (zoom * base - frame)));
  return {
    x: axis(bounded.offsetX, imgW * cover, frameW),
    y: axis(bounded.offsetY, imgH * cover, frameH),
    zoom: Math.round(zoom * 100) / 100,
  };
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };
    img.src = url;
  });
}

/**
 * Compute normalized focal point from pan/zoom within aspect frame.
 */
export function focalFromTransform(
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
  transform: CropTransform
): { x: number; y: number } {
  const { scale, offsetX, offsetY } = transform;
  const displayedW = imgW * scale;
  const displayedH = imgH * scale;
  const centerX = frameW / 2 + offsetX;
  const centerY = frameH / 2 + offsetY;
  const imgLeft = centerX - displayedW / 2;
  const imgTop = centerY - displayedH / 2;
  const focalX = (frameW / 2 - imgLeft) / displayedW;
  const focalY = (frameH / 2 - imgTop) / displayedH;
  return {
    x: Math.max(0, Math.min(1, focalX)),
    y: Math.max(0, Math.min(1, focalY)),
  };
}

/**
 * Export cropped JPEG blob for identity/logo surfaces.
 */
export async function exportCroppedImage(
  file: File,
  preset: CropPreset,
  transform: CropTransform,
  frameSize = 320
): Promise<CropResult> {
  const img = await loadImageFromFile(file);
  const frameH = frameSize;
  const frameW = Math.round(frameSize * preset.aspect);
  const focal = focalFromTransform(img.width, img.height, frameW, frameH, transform);

  const canvas = document.createElement('canvas');
  const outSize = preset.aspect >= 1 ? 1024 : Math.round(1024 * preset.aspect);
  canvas.width = preset.aspect >= 1 ? outSize : Math.round(outSize * preset.aspect);
  canvas.height = preset.aspect >= 1 ? Math.round(outSize / preset.aspect) : outSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');

  const { scale, offsetX, offsetY } = transform;
  const displayedW = img.width * scale * (canvas.width / frameW);
  const displayedH = img.height * scale * (canvas.height / frameH);
  const dx = (canvas.width / 2 + offsetX * (canvas.width / frameW)) - displayedW / 2;
  const dy = (canvas.height / 2 + offsetY * (canvas.height / frameH)) - displayedH / 2;

  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, dx, dy, displayedW, displayedH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Export failed'))),
      'image/jpeg',
      0.88
    );
  });

  return { blob, focal };
}

export async function exportFocalOnly(
  file: File,
  preset: CropPreset,
  transform: CropTransform,
  frameSize = 320
): Promise<{ focal: { x: number; y: number; zoom: number } }> {
  const img = await loadImageFromFile(file);
  const frameW = Math.round(frameSize * preset.aspect);
  const frameH = frameSize;
  return { focal: framingFromTransform(img.width, img.height, frameW, frameH, transform) };
}

/** Min scale so image covers the crop frame. */
export function initialCoverScale(
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number
): number {
  return Math.max(frameW / imgW, frameH / imgH);
}
